import { Box3, Group, Vector3, type Object3D } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { logWarn } from '../core/Debug';

/** The CC0 model that stands in for each box piece, relative to the base URL. */
export const DEFAULT_PIECE_MODELS: Record<string, string> = {
  crate: 'box.glb',
  console: 'desk_computer.glb',
  pillar: 'structure.glb',
  barrier: 'gate_simple.glb',
};

interface Template {
  scene: Object3D;
  size: Vector3;
  centre: Vector3;
}

/** The bit of `GLTFLoader` this needs, so a test can stand in for it. */
export interface ModelLoader {
  loadAsync(url: string): Promise<{ scene: Object3D }>;
}

/**
 * The level's prop models, loaded once and cloned per placement.
 *
 * The models are Kenney's CC0 space kit, vendored under `public/assets/models`
 * (see `docs/ASSET_CREDITS.md`). Loading is optional by design: the level draws
 * its procedural boxes until this is ready, and forever if a file is missing,
 * so a flaky download can never stop a run.
 */
export class ModelLibrary {
  private readonly templates = new Map<string, Template>();
  private started = false;
  private settled = false;
  private resolveReady: (() => void) | null = null;
  private readonly readyPromise: Promise<void>;

  constructor(
    private readonly baseUrl = '/assets/models/kenney/',
    private readonly files: Record<string, string> = DEFAULT_PIECE_MODELS,
    private readonly loader: ModelLoader = new GLTFLoader(),
  ) {
    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });
  }

  /** True once every model has loaded or failed. */
  get isSettled(): boolean {
    return this.settled;
  }

  /** Resolves when the load pass finishes, however many models made it. */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Start loading. Called by the game when it has a real renderer: a test
   * harness has no use for the meshes and should not wait on network I/O.
   */
  load(): void {
    if (this.started) return;
    this.started = true;
    const jobs = Object.entries(this.files).map(async ([kind, file]) => {
      try {
        const gltf = await this.loader.loadAsync(`${this.baseUrl}${file}`);
        const scene = gltf.scene;
        scene.updateMatrixWorld(true);
        const box = new Box3().setFromObject(scene);
        const size = new Vector3();
        const centre = new Vector3();
        box.getSize(size);
        box.getCenter(centre);
        this.templates.set(kind, { scene, size, centre });
      } catch (error) {
        logWarn(`model "${kind}" (${file}) could not be loaded; using the box shape`, error);
      }
    });
    void Promise.all(jobs).then(() => {
      this.settled = true;
      this.resolveReady?.();
      this.resolveReady = null;
    });
  }

  /**
   * A clone of a piece's model, uniformly scaled to fit a placement box and
   * centred on it. Null until that model is loaded (or if it never loads).
   */
  instance(kind: string, size: [number, number, number]): Object3D | null {
    const template = this.templates.get(kind);
    if (!template) return null;
    const fit = Math.min(
      size[0] / Math.max(template.size.x, 1e-4),
      size[1] / Math.max(template.size.y, 1e-4),
      size[2] / Math.max(template.size.z, 1e-4),
    );
    const clone = template.scene.clone(true);
    // Each placement gets its own geometry and material instances: the builder
    // disposes whatever it rebuilds, and shared resources would take the
    // templates down with them.
    clone.traverse((object) => {
      const mesh = object as Object3D & {
        geometry?: { clone(): unknown };
        material?: { clone(): unknown } | Array<{ clone(): unknown }>;
      };
      if (mesh.geometry) mesh.geometry = mesh.geometry.clone() as typeof mesh.geometry;
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map((entry) => entry.clone()) as typeof mesh.material;
      } else if (mesh.material) {
        mesh.material = mesh.material.clone() as typeof mesh.material;
      }
    });
    clone.scale.setScalar(fit);
    clone.position.set(
      -template.centre.x * fit,
      -template.centre.y * fit,
      -template.centre.z * fit,
    );
    const wrapper = new Group();
    wrapper.add(clone);
    return wrapper;
  }
}
